import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
// @ts-ignore
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSellerMetrics, SellerFunnel } from '../../hooks/useSellerMetrics';
import { useMarketplaceConnections } from '../../hooks/useMarketplaceConnections';
import { SellerAutomationHeader, SellerRunButton, sellerHeaderSelectClass } from './SellerAutomationHeader';
import { useMenuPermissions } from '../../contexts/MenuPermissionsContext';
import { useSellerAnalysis, type HealthCard } from '../../hooks/useSellerAnalysis';
import { useSellerAdsBoard, type AdsBoardCard } from '../../hooks/useSellerAdsAgent';
import { useSellerAutomation } from '../../hooks/useSellerAutomation';
import { URGENCY_BADGE, URGENCY_ORDER, useSellerSettings } from './sellerUi';
import { useSellerAgentPending } from '../../hooks/useSellerAgentPending';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('pt-BR').format(value);
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return <span className="text-xs text-slate-400">—</span>;
  const isPositive = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
      {isPositive ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

// Tela "Hoje": numeros da conta + uma coluna "O que fazer agora" que junta as
// pendencias das outras telas (padrao Business Advice da Shopee / Quick tasks da
// Amazon). Cada bloco so monta se o usuario tiver permissao para a tela de origem,
// assim nenhum hook dispara requisicao para area que ele nao enxerga.
export function SellerTodayView() {
  const { canSee } = useMenuPermissions();
  const [days, setDays] = useState<7 | 30>(7);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [visibleLines, setVisibleLines] = useState({ revenue: true, orders: true, visits: true });

  const { connections } = useMarketplaceConnections();
  const { data, loading, syncing, error, syncMetrics } = useSellerMetrics(selectedConnection, days);

  // Extensao conectada nao significa acesso: a sincronizacao depende de sessao e headers validos.
  const degradedConnections = connections.filter((c) => c.health.state !== 'ok' && c.health.state !== 'unknown');

  // O Mercado Livre não fornece cliques/impressões — mostramos "n/d" quando uma conexão ML está selecionada.
  const selectedMarketplace = connections.find((c) => c.connectionId === selectedConnection)?.marketplace;
  const isMlSelected = selectedMarketplace === 'mercadolivre';

  const toggleLine = (key: 'revenue' | 'orders' | 'visits') => {
    setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Precisa ficar ANTES do empty state: com zero conexoes o componente retorna
  // cedo, e um hook depois desse ponto sumiria entre renders (React #300).
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // Empty state
  if (!loading && !data?.hasData && connections.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-100">
            <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Conecte sua extensão</h3>
          <p className="mt-2 text-sm text-slate-500">
            Instale e conecte a extensão Shopee no navegador para começar a ver suas métricas de vendas aqui.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {degradedConnections.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-semibold">Extensão conectada, mas sem acesso a alguns dados</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {degradedConnections.map((c) => {
              const failing = c.health.checks.filter((k) => !k.ok).map((k) => k.label);
              return (
                <li key={`${c.marketplace}:${c.connectionId}`}>
                  <span className="font-medium">{c.connectionName || c.providerAccountId || (c.marketplace === 'mercadolivre' ? 'Mercado Livre' : 'Shopee')}:</span>{' '}
                  {c.health.state === 'outdated' ? 'extensão desatualizada' : c.health.state === 'logged_out' ? 'conta deslogada no navegador' : `indisponível: ${failing.join(', ')}`}
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs">
            Abra a extensão no navegador e clique em <span className="font-semibold">"Abrir abas para sincronização de headers"</span>. Se alguma aba pedir login, entre na conta e repita.
          </p>
        </div>
      )}
      <SellerAutomationHeader
        title="Hoje"
        subtitle="Como a conta está indo e o que precisa da sua atenção."
        automationKeys={[]}
        actions={
          <>
            {/* Period toggle */}
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button
                onClick={() => setDays(7)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${days === 7 ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              >
                7 dias
              </button>
              <button
                onClick={() => setDays(30)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${days === 30 ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              >
                30 dias
              </button>
            </div>

            {/* Connection selector */}
            {connections.length > 0 && (
              <select
                value={selectedConnection || ''}
                onChange={(e) => setSelectedConnection(e.target.value || null)}
                className={sellerHeaderSelectClass}
              >
                <option value="">Todas conexões</option>
                {connections.map((c) => (
                  <option key={c.connectionId} value={c.connectionId}>
                    {c.connectionName || c.providerAccountId || 'Extensão'}
                  </option>
                ))}
              </select>
            )}

            {/* Sync button */}
            <SellerRunButton
              label="Sincronizar"
              runningLabel="Sincronizando..."
              running={syncing}
              disabled={connections.length === 0}
              variant="primary"
              onClick={() => {
                if (!selectedConnection) {
                  syncMetrics(undefined, undefined, connections.map((c) => ({ connectionId: c.connectionId, marketplace: c.marketplace })));
                  return;
                }
                const mkt = connections.find(c => c.connectionId === selectedConnection)?.marketplace || 'shopee';
                syncMetrics(selectedConnection, mkt);
              }}
            />
          </>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="min-w-0 space-y-5">
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-orange-600 border-t-transparent" />
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <KpiCard label="Receita" value={formatCurrency(data.summary.revenue)} delta={data.comparison.revenue.deltaPercent} />
            <KpiCard label="Pedidos" value={formatNumber(data.summary.orders)} delta={data.comparison.orders.deltaPercent} />
            <KpiCard label="Visitas" value={formatNumber(data.summary.visits)} delta={data.comparison.visits.deltaPercent} />
            <KpiCard label="Cliques" value={isMlSelected ? 'n/d' : formatNumber(data.summary.clicks)} delta={0} />
            <KpiCard label="Conversão" value={`${data.summary.visits > 0 ? ((data.summary.orders / data.summary.visits) * 100).toFixed(2) : '0'}%`} delta={data.comparison.conversion?.deltaPercent ?? 0} />
          </div>

          {/* Funil de vendas (Mercado Livre) */}
          {data.funnel && <SalesFunnel funnel={data.funnel} />}

          {/* Chart */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Performance Temporal</h2>
              <div className="flex gap-2">
                <button onClick={() => toggleLine('revenue')} className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition ${visibleLines.revenue ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-400'}`}>Receita</button>
                <button onClick={() => toggleLine('orders')} className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition ${visibleLines.orders ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>Pedidos</button>
                <button onClick={() => toggleLine('visits')} className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition ${visibleLines.visits ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>Visitas</button>
              </div>
            </div>

            {data.series.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.series} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={(d: string) => { const parts = d.split('-'); return `${parts[2]}/${parts[1]}`; }} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}
                    formatter={(value: number, name: string) => {
                      if (name === 'revenue') return [formatCurrency(value), 'Receita'];
                      if (name === 'orders') return [formatNumber(value), 'Pedidos'];
                      return [formatNumber(value), 'Visitas'];
                    }}
                    labelFormatter={(d: string) => { const parts = d.split('-'); return `${parts[2]}/${parts[1]}/${parts[0]}`; }}
                  />
                  {visibleLines.revenue && <Line type="monotone" dataKey="revenue" stroke="#ea580c" strokeWidth={2} dot={false} />}
                  {visibleLines.orders && <Line type="monotone" dataKey="orders" stroke="#2563eb" strokeWidth={2} dot={false} />}
                  {visibleLines.visits && <Line type="monotone" dataKey="visits" stroke="#059669" strokeWidth={2} dot={false} />}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-48 items-center justify-center text-sm text-slate-400">
                Sem dados para o período selecionado. Clique em Sincronizar.
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex h-64 items-center justify-center text-sm text-slate-500">
          Selecione uma conexão e sincronize para ver os dados.
        </div>
      )}
      </div>

      <aside className="min-w-0 space-y-3">
        <h2 className="text-sm font-bold text-gray-800">O que fazer agora</h2>
        {canSee('seller.agente') && <AgentApprovalsTasks />}
        {canSee('seller.health') && <ListingsTasks />}
        {canSee('seller.ads-health') && <AdsTasks />}
        <AutomationsTasks />
      </aside>
      </div>
    </div>
  );
}

// ---- O que fazer agora -------------------------------------------------------

function TaskCard({ title, count, tone, children, cta }: { title: string; count: number; tone: 'red' | 'amber' | 'green' | 'gray'; children?: ReactNode; cta?: { label: string; to: string } }) {
  const toneCls = { red: 'bg-red-100 text-red-700', amber: 'bg-amber-100 text-amber-700', green: 'bg-emerald-100 text-emerald-700', gray: 'bg-gray-100 text-gray-500' }[tone];
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-gray-800">{title}</h3>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${toneCls}`}>{count}</span>
      </div>
      {children && <div className="mt-2 space-y-1.5">{children}</div>}
      {cta && (
        <Link to={cta.to} className="mt-3 inline-flex text-xs font-semibold text-orange-700 hover:text-orange-800 hover:underline">
          {cta.label} →
        </Link>
      )}
    </section>
  );
}

function TaskLine({ image, title, meta, badge }: { image?: string | null; title: string; meta?: string | null; badge?: { label: string; cls: string } }) {
  return (
    <div className="flex items-center gap-2.5 rounded-md bg-gray-50 px-2 py-1.5">
      {image !== undefined && (image ? <img src={image} alt="" className="h-8 w-8 shrink-0 rounded object-cover" /> : <div className="h-8 w-8 shrink-0 rounded bg-gray-200" />)}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-medium text-gray-800" title={title}>{title}</p>
        {meta && <p className="truncate text-[10px] text-gray-500">{meta}</p>}
      </div>
      {badge && <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>}
    </div>
  );
}

function ListingsTasks() {
  const { columns, loading } = useSellerAnalysis();
  const pending: HealthCard[] = columns
    ? [...columns.critico, ...columns.problema, ...columns.atencao].filter((c) => c.reviewStatus === 'pendente')
    : [];
  pending.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 9) - (URGENCY_ORDER[b.urgency] ?? 9));
  const inProgress = columns ? columns.revisados.filter((c) => c.reviewStatus === 'revisado').length : 0;

  if (loading && !columns) return <TaskCard title="Anúncios para revisar" count={0} tone="gray"><p className="text-[11px] text-gray-400">Carregando...</p></TaskCard>;
  return (
    <TaskCard
      title="Anúncios para revisar"
      count={pending.length}
      tone={pending.some((c) => c.urgency === 'urgente' || c.urgency === 'alta') ? 'red' : pending.length ? 'amber' : 'green'}
      cta={{ label: pending.length ? 'Abrir quadro de anúncios' : 'Ver anúncios', to: '/seller/anuncios?view=quadro' }}
    >
      {pending.length === 0 ? (
        <p className="text-[11px] text-gray-500">Nenhum anúncio com problema pendente.{inProgress ? ` ${inProgress} em revisão.` : ''}</p>
      ) : (
        pending.slice(0, 3).map((c) => (
          <TaskLine key={c.id} image={c.imageUrl} title={c.title || c.listingIdExt} meta={c.headline || c.stateLabel} badge={URGENCY_BADGE[c.urgency]} />
        ))
      )}
    </TaskCard>
  );
}

function AdsTasks() {
  const { columns, alerts, loading } = useSellerAdsBoard();
  const cards: AdsBoardCard[] = columns ? Object.values(columns).flat() : [];
  const withActions = cards.filter((c) => c.pendingActionsCount > 0);
  const critical = cards.filter((c) => c.column === 'critico' && c.reviewStatus === 'pendente');
  const total = alerts.length + withActions.length + critical.length;

  if (loading && !columns) return <TaskCard title="Ads" count={0} tone="gray"><p className="text-[11px] text-gray-400">Carregando...</p></TaskCard>;
  return (
    <TaskCard
      title="Ads"
      count={total}
      tone={critical.length || alerts.some((a) => a.severity === 'critica') ? 'red' : total ? 'amber' : 'green'}
      cta={{ label: total ? 'Abrir quadro de Ads' : 'Ver campanhas', to: '/seller/ads?view=quadro' }}
    >
      {total === 0 && <p className="text-[11px] text-gray-500">Nenhum alerta nem ação pendente.</p>}
      {alerts.slice(0, 2).map((a) => (
        <TaskLine key={a.id} title={a.title} meta={a.campaignName || a.message} badge={{ label: 'Alerta', cls: a.severity === 'critica' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700' }} />
      ))}
      {withActions.length > 0 && (
        <TaskLine title={`${withActions.reduce((n, c) => n + c.pendingActionsCount, 0)} ação(ões) da IA prontas pra executar`} meta={withActions.slice(0, 2).map((c) => c.campaignName).join(', ')} badge={{ label: 'Executar', cls: 'bg-emerald-100 text-emerald-700' }} />
      )}
      {critical.slice(0, 2).map((c) => (
        <TaskLine key={c.id} title={c.campaignName} meta={c.headline || `ROAS ${c.metrics.roas}x · gasto ${formatCurrency(c.metrics.spend)}`} badge={{ label: 'Crítica', cls: 'bg-red-100 text-red-700' }} />
      ))}
    </TaskCard>
  );
}

// Alterações pedidas ao Agente (no chat ou por chave MCP) esperando aprovação.
function AgentApprovalsTasks() {
  const { total, sessions } = useSellerAgentPending(true);
  if (!total) return null;
  return (
    <TaskCard title="Alterações esperando sua aprovação" count={total} tone="amber">
      {sessions.slice(0, 4).map((s) => (
        <Link key={s.id} to={`/seller/agente?conversa=${s.id}`} className="block hover:opacity-80">
          <TaskLine title={s.title} meta={s.source === 'mcp' ? 'Pedido por chave MCP' : 'Conversa com o agente'} badge={{ label: `${s.pending} pendente${s.pending > 1 ? 's' : ''}`, cls: 'bg-amber-100 text-amber-800' }} />
        </Link>
      ))}
    </TaskCard>
  );
}

function AutomationsTasks() {
  const { automations } = useSellerAutomation();
  const { openSettings, canConfigure } = useSellerSettings();
  if (automations.length === 0) return null;
  const on = automations.filter((a) => a.enabled).length;
  return (
    <TaskCard title="Automações ligadas" count={on} tone={on ? 'green' : 'gray'}>
      <div className="flex flex-wrap gap-1.5">
        {automations.map((a) => (
          <button
            key={a.key}
            type="button"
            disabled={!canConfigure}
            onClick={() => openSettings(a.settingsTab)}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2 py-0.5 text-[10px] text-gray-600 enabled:hover:border-orange-300 enabled:hover:bg-orange-50/50"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${a.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
            {a.label}
          </button>
        ))}
      </div>
    </TaskCard>
  );
}


function SalesFunnel({ funnel }: { funnel: SellerFunnel }) {
  const tc = funnel.totalConversion;
  const isDown = tc.state === 'down';
  const hasDelta = tc.deltaPoints != null && tc.state;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-800">Funil de vendas</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Conversão total</span>
          <span className="text-base font-bold text-slate-900">{tc.current != null ? `${tc.current.toString().replace('.', ',')}%` : '—'}</span>
          {hasDelta && (
            <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isDown ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
              {isDown ? '↓' : '↑'} {Math.abs(tc.deltaPoints as number).toString().replace('.', ',')} pts
            </span>
          )}
          {tc.previous != null && (
            <span className="text-[10px] text-slate-400">ant.: {tc.previous.toString().replace('.', ',')}%</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {funnel.stages.map((stage, i) => (
          <div key={stage.id} className="relative rounded-lg border border-slate-100 bg-slate-50/60 p-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{stage.label}</p>
            <p className="mt-1 text-lg font-bold text-slate-900">{stage.value ?? '—'}</p>
            {stage.secondary && <p className="text-xs text-slate-500">{stage.secondary}</p>}
            {i > 0 && stage.pct != null && (
              <span className="absolute right-3 top-3 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                {stage.pct.toString().replace('.', ',')}%
              </span>
            )}
          </div>
        ))}
      </div>

      {funnel.comparisonLabel && (
        <p className="mt-3 text-[11px] text-slate-400">{funnel.comparisonLabel}</p>
      )}
    </div>
  );
}

function KpiCard({ label, value, delta }: { label: string; value: string; delta: number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-slate-900">{value}</p>
      <div className="mt-1">
        <DeltaBadge delta={delta} />
      </div>
    </div>
  );
}
